import _ from 'lodash';
import { Action, Dispatch } from 'redux';
import moment from 'moment';
import MomentTz from 'moment-timezone';
import { isArrayEqual } from '../utils';
import { IAppState } from './reducers';
import {
  getRecentlyCreated,
  deleteAssetStatus,
  updateAssetStatus,
  deleteManyAssetStatus,
} from '../api/vault/assetStatus/api';
import {
  ActionStatus,
  IAssetStatus, ActionStatus as JobStatus, ActionType as JobType,
} from '../api/vault/assetStatus/interfaces';
import {
  selectHasManageLiveClippingPermission,
  selectIsUserVaultSuperAdmin,
  selectUserId,
  selectUserOrgIds,
  selectUserRole,
} from '../state/user/userOrgRole';
import { getAssetFromApi } from '../components/AssetDetails/api/assetsLookupsMetatags';
import { postVirtualClipFromApi } from '../components/AssetDetails/api/assetsVirtualClips';
import { getVideoTypeFromProduct, selectIsExchange } from './product';
import { queryGamesAction } from './search';
import { queryGames } from './exchange/searchResults';
import { Routes, VAULT_VIDEO_TYPE } from '../constants';
import toast, { superAdminToast } from '../utils/toast';
import { fetchLiveEvents, IFetchLiveEventsJobsParams } from './liveEvents/jobsLiveEvents';
import { getDownloadLink } from '../api/vault/assetDetails';
import { deepCopy } from '../utils/deepCopy';
import { statusToText } from '../components/Shared/JobList/statusToTextMap';

const downloadTypes = [JobType.Download, JobType.PushToCloud];
const publishTypes = [JobType.Publish, JobType.PublishTwitter, JobType.PublishYahoo, JobType.PublishYoutube];
const uploadTypes = [JobType.Upload];
const createTypes = [JobType.Create];

const activeStatuses = [ActionStatus.Queued, ActionStatus.InProgress, ActionStatus.Transcoding];

export enum JobTab {
  Upload = 'Upload',
  Download = 'Download',
  Create = 'Create',
  Publish = 'Publish',
  LiveEvents = 'Live Events',
  ExchangeEvents = 'Exchange Events',
  ExchangeCreate = 'Exchange Create',
}

export type Order = 'asc' | 'desc';
export const defaultOrder: Order = 'asc';

export interface IJobData {
  all: IAssetStatus[];
  queued: IAssetStatus[];
  completed: IAssetStatus[];
  failed: IAssetStatus[];
  inProgress: IAssetStatus[];
  completedIds: string[];
  active: IAssetStatus[];
}

export interface IAllJobData {
  uploads: IJobData;
  downloads: IJobData;
  publishes: IJobData;
  creates: IJobData;
  exchangeCreates: IJobData;
}

// Job Data
const defaultJobData: IJobData = {
  all: [],
  queued: [],
  completed: [],
  failed: [],
  inProgress: [],
  completedIds: [],
  active: [],
};
const getDefaultAllJobData = (): IAllJobData => ({
  ...{
    uploads: deepCopy(defaultJobData),
    downloads: deepCopy(defaultJobData),
    publishes: deepCopy(defaultJobData),
    creates: deepCopy(defaultJobData),
    exchangeCreates: deepCopy(defaultJobData),
  },
});

export interface IFormattedAssetStatus extends IAssetStatus {
  actionTypeText: string;
  date: string;
  time: string;
  actionStatusText: string;
  progress: string;
}

export interface IState {
  jobs: IAssetStatus[];
  filteredJobs: IFormattedAssetStatus[];
  jobTab: JobTab;
  jobData: IAllJobData,
  currentJobData: IJobData,
  completedUploads: { [key: string]: IAssetStatus };
  isOpen: boolean;
  isLoading: boolean;
  isRemoveJobsOpen: boolean,
  order: Order;
  orderBy: keyof IAssetStatus;
  statusFilter: string;
  isRetryDisabled: boolean;
  backgroundTask: number;
  deletedAssetStatuses: IAssetStatus[];
}

export enum ActionType {
  Open = 'JobStatus.Open',
  Close = 'JobStatus.Close',
  ToggleRemoveJobs = 'JobStatus.ToggleRemoveJobs',
  StartLoading = 'JobStatus.StartLoading',
  FinishLoading = 'JobStatus.FinishLoading',
  UpdateJobs = 'JobStatus.UpdateJobs',
  ClearJobs = 'JobStatus.ClearJobs',
  UpdateSortingOrder = 'JobStatus.UpdateSortingOrder',
  ToggleStatusFilter = 'JobStatus.ToggleStatusFilter',
  RemoveJob = 'JobStatus.RemoveJob',
  UpdateJobTab = 'JobStatus.UpdateJobTab',
  UpdateRetryDisabled = 'JobStatus.UpdateRetryDisabled',
  StartBackgroundTask = 'JobStatus.StartBackgroundTask',
  StopBackgroundTask = 'JobStatus.StopBackgroundTask',
  UpdateDeletedAssetStatuses = 'JobStatus.UpdateDeletedAssetStatuses',
}

export interface IJobStatusReviewAction extends Action<ActionType> {
  payload?: any,
}

const INITIAL_STATE: IState = {
  jobs: [],
  filteredJobs: [],
  jobTab: JobTab.Upload,
  jobData: getDefaultAllJobData(),
  currentJobData: deepCopy(defaultJobData),
  completedUploads: {},
  isOpen: false,
  isLoading: false,
  isRemoveJobsOpen: false,
  order: 'desc',
  orderBy: 'actionDate',
  statusFilter: 'All',
  isRetryDisabled: false,
  backgroundTask: -1,
  deletedAssetStatuses: [],
};

export default function reducer(state: IState = INITIAL_STATE, action: IJobStatusReviewAction): IState {
  switch (action.type) {
    case ActionType.Open:
      return {
        ...state,
        isOpen: true,
      };
    case ActionType.Close:
      return {
        ...state,
        isOpen: false,
      };
    case ActionType.ToggleRemoveJobs:
      return {
        ...state,
        isRemoveJobsOpen: !state.isRemoveJobsOpen,
      };
    case ActionType.StartLoading:
      return {
        ...state,
        isLoading: true,
      };
    case ActionType.FinishLoading:
      return {
        ...state,
        isLoading: false,
      };
    case ActionType.UpdateJobs:
      return {
        ...state,
        filteredJobs: action.payload.filteredJobs,
        jobs: action.payload.jobs,
        completedUploads: action.payload.completedUploads,
        jobData: action.payload.jobData,
        currentJobData: action.payload.currentJobData,
      };
    case ActionType.UpdateSortingOrder:
      return {
        ...state,
        filteredJobs: action.payload.filteredJobs,
        orderBy: action.payload.orderBy,
        order: action.payload.order,
      };
    case ActionType.ToggleStatusFilter:
      return {
        ...state,
        statusFilter: action.payload.statusFilter,
        filteredJobs: action.payload.filteredJobs,
      };
    case ActionType.RemoveJob: {
      const jobs = state.jobs.filter(({ _id }) => _id !== action.payload.id);
      return {
        ...state,
        jobs,
        jobData: action.payload.jobData,
        currentJobData: getCurrentJobData(action.payload.jobData, state.jobTab),
        filteredJobs: getFilteredAndSortedJobs(jobs, state.jobTab, state.statusFilter, state.orderBy, state.order),
      };
    }
    case ActionType.ClearJobs:
      return {
        ...state,
        jobs: INITIAL_STATE.jobs,
        jobTab: INITIAL_STATE.jobTab,
        jobData: INITIAL_STATE.jobData,
        currentJobData: INITIAL_STATE.currentJobData,
        order: INITIAL_STATE.order,
        orderBy: INITIAL_STATE.orderBy,
      };
    case ActionType.UpdateJobTab:
      return {
        ...state,
        jobTab: action.payload.jobTab,
        filteredJobs: getFilteredAndSortedJobs(state.jobs, action.payload.jobTab, state.statusFilter, state.orderBy, state.order),
        currentJobData: getCurrentJobData(state.jobData, action.payload.jobTab),
      };
    case ActionType.UpdateRetryDisabled:
      return {
        ...state,
        isRetryDisabled: action.payload,
      };
    case ActionType.StartBackgroundTask:
      return {
        ...state,
        backgroundTask: action.payload.backgroundTask,
      };
    case ActionType.StopBackgroundTask:
      return {
        ...state,
        backgroundTask: INITIAL_STATE.backgroundTask,
      };
    case ActionType.UpdateDeletedAssetStatuses:
      return {
        ...state,
        deletedAssetStatuses: action.payload.deletedAssetStatuses,
      };
    default:
      return state;
  }
}

export const toggleRemoveJobs = () => ({
  type: ActionType.ToggleRemoveJobs,
});

const getCurrentJobData = (jobData: IAllJobData, jobTab: JobTab) => {
  let currentJobData: IJobData = deepCopy(defaultJobData);
  if (jobTab !== JobTab.LiveEvents) {
    if (jobTab === JobTab.Download) currentJobData = jobData.downloads;
    if (jobTab === JobTab.Upload) currentJobData = jobData.uploads;
    if (jobTab === JobTab.Publish) currentJobData = jobData.publishes;
    if (jobTab === JobTab.Create) currentJobData = jobData.creates;
    if (jobTab === JobTab.ExchangeCreate) currentJobData = jobData.exchangeCreates;
  }
  return currentJobData;
};

const getFilteredAndSortedJobs = (jobs: IAssetStatus[], jobTab: JobTab, statusFilter: string, orderBy: string, order: Order) => {
  const getActionTypeText = (job: IAssetStatus) => {
    if (job.actionType === JobType.Publish) return 'LAN/SAN';
    if (job.actionType === JobType.PublishTwitter) return 'X';
    return job.actionType.charAt(0).toUpperCase() + job.actionType.slice(1);
  };

  let filteredJobs: IFormattedAssetStatus[] = jobs.map((job) => ({
    ...job,
    date: `${job.localizedActionDate.split(' ')[0]} `,
    time: `${job.localizedActionDate.split(' ')[1]} ${job.localizedActionDate.split(' ')[2]}`,
    actionTypeText: getActionTypeText(job),
    progress: job.actionProgress ? `${job.actionProgress}%` : 'N/A',
    actionStatusText: statusToText[job.actionStatus],
  })).filter(({ actionStatus }) => actionStatus !== ActionStatus.Cancelled); // Exclude cancelled jobs
  if (statusFilter !== 'All') filteredJobs = filteredJobs.filter(({ actionStatus }) => actionStatus === statusFilter);
  if (jobTab === JobTab.Download) {
    filteredJobs = filteredJobs.filter(({ actionType }) => downloadTypes.includes(actionType));
  } else if (jobTab === JobTab.Publish) {
    filteredJobs = filteredJobs.filter(({ actionType }) => publishTypes.includes(actionType));
  } else if (jobTab === JobTab.Upload) {
    filteredJobs = filteredJobs.filter(({ actionType }) => uploadTypes.includes(actionType));
  } else if (jobTab === JobTab.ExchangeCreate) {
    filteredJobs = filteredJobs.filter(({ actionType, videoType }) => createTypes.includes(actionType) && videoType === VAULT_VIDEO_TYPE.Exchange);
  } else if (jobTab === JobTab.Create) {
    filteredJobs = filteredJobs.filter(({ actionType, videoType }) => createTypes.includes(actionType) && videoType !== VAULT_VIDEO_TYPE.Exchange);
  } else {
    return null;
  }

  // Sort
  let sortedJobs: IFormattedAssetStatus[];
  switch (orderBy) {
    case 'actionMachine':
    case 'actionType':
    case 'actionStatus':
    case 'user':
    case 'assetName':
      sortedJobs = filteredJobs.sort((a: any, b: any) => {
        const fieldA = a[orderBy] || '';
        const fieldB = b[orderBy] || '';
        return order === 'asc' ? fieldA.localeCompare(fieldB) : fieldB.localeCompare(fieldA);
      });
      break;
    case 'actionDate':
      sortedJobs = _.orderBy<IFormattedAssetStatus>(filteredJobs, (job) => [moment(job.actionDate).unix()], [order]);
      break;
    default:
      sortedJobs = _.orderBy<IFormattedAssetStatus>(filteredJobs, (job) => [orderBy], [order]);
  }
  return sortedJobs;
};

const removeJobFromAllJobData = (jobId: string, jobData: IAllJobData): IAllJobData => {
  const tempJobData = jobData;
  const removeJob = (job: IAssetStatus, data: IJobData) => {
    const tempData = data;
    if (job) {
      tempData.all = data.all.filter((x) => x._id !== job._id);
      tempData.completed = data.completed.filter((x) => x._id !== job._id);
      tempData.queued = data.queued.filter((x) => x._id !== job._id);
      tempData.failed = data.failed.filter((x) => x._id !== job._id);
      tempData.inProgress = data.inProgress.filter((x) => x._id !== job._id);
      tempData.completedIds = data.completedIds.filter((x) => x !== job._id);
    }
    return tempData;
  };

  tempJobData.uploads = removeJob(jobData.uploads.all.find((x) => x._id === jobId), jobData.uploads);
  tempJobData.downloads = removeJob(jobData.downloads.all.find((x) => x._id === jobId), jobData.downloads);
  tempJobData.publishes = removeJob(jobData.publishes.all.find((x) => x._id === jobId), jobData.publishes);
  tempJobData.creates = removeJob(jobData.creates.all.find((x) => x._id === jobId), jobData.creates);
  tempJobData.exchangeCreates = removeJob(jobData.exchangeCreates.all.find((x) => x._id === jobId), jobData.exchangeCreates);
  return { ...tempJobData };
};

const getJobData = (jobs: IAssetStatus[]) => {
  const jobData = getDefaultAllJobData();

  const addJob = (job: IAssetStatus, data: IJobData) => {
    data.all.push(job);
    if (job.actionStatus === JobStatus.Completed) {
      data.completed.push(job);
      data.completedIds.push(job._id);
    } else if (job.actionStatus === JobStatus.Queued) {
      data.queued.push(job);
    } else if (job.actionStatus === JobStatus.Failed) {
      data.failed.push(job);
    } else if (job.actionStatus === JobStatus.InProgress) {
      data.inProgress.push(job);
    }

    if (activeStatuses.includes(job.actionStatus)) {
      data.active.push(job);
    }
  };

  jobs.forEach((job) => {
    if (uploadTypes.includes(job.actionType)) {
      addJob(job, jobData.uploads);
    } else if (downloadTypes.includes(job.actionType)) {
      addJob(job, jobData.downloads);
    } else if (publishTypes.includes(job.actionType)) {
      addJob(job, jobData.publishes);
    } else if (createTypes.includes(job.actionType) && job.videoType === VAULT_VIDEO_TYPE.Exchange) {
      addJob(job, jobData.exchangeCreates);
    } else if (createTypes.includes(job.actionType) && job.videoType !== VAULT_VIDEO_TYPE.Exchange) {
      addJob(job, jobData.creates);
    }
  });

  return jobData;
};

interface IJobInfo {
  jobs: IAssetStatus[],
  filteredJobs: IAssetStatus[],
  completedUploads: { [key: string]: IAssetStatus },
  jobData: IAllJobData,
  currentJobData: IJobData,
}

export const queryJobs = (videoType: string[], refreshOnUpload: boolean = false, isVaultSuperAdmin: boolean = false, liveEventsParams: IFetchLiveEventsJobsParams = null) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const state = getState();
  const {
    jobStatus: {
      isLoading, statusFilter, jobs: oldJobs, orderBy, order, jobTab,
    },
  } = state;
  // Query live events
  if (liveEventsParams) fetchLiveEvents(liveEventsParams)(dispatch, getState);

  if (isLoading) return; // Don't allow multiple job queries at once
  const [orgId] = selectUserOrgIds(state);
  let { jobStatus: { completedUploads } } = state;
  const initialQuery = !Array.isArray(oldJobs);

  const {
    editMetadata: {
      isEditable: isSearchEdit,
    },
    exchange: {
      activeAsset: {
        isEditable: isExchangeEdit,
      },
    },
  } = state;

  const isVaultSuperAdmin = selectIsUserVaultSuperAdmin(state);

  dispatch({
    type: ActionType.StartLoading,
  });

  let jobs = oldJobs ? [...oldJobs] : [];
  let jobInfo: IJobInfo;
  try {
    let includeExchangeEvents = false;
    if (videoType.includes(VAULT_VIDEO_TYPE.Vault) && isVaultSuperAdmin) {
      includeExchangeEvents = true;
    }

    const response = await getRecentlyCreated(orgId, false, videoType, includeExchangeEvents);
    jobs = (response.data?.data || []).map((job) => ({
      ...job,
      localizedActionDate: MomentTz.tz(job.actionDate, MomentTz.tz.guess()).format('L LT'),
    }));
  } catch (e) {
    console.error('[QA] Error encountered fetching job status', e.toString());
  }

  const diff = !isArrayEqual(oldJobs, jobs);
  // diff checking between existing and incoming jobs
  // this will prevent the table from shuffling items on refresh if there are no changes
  if (diff) {
    // if refresh on upload and diff found
    if (refreshOnUpload) {
      const newCompletedUploads: { [key: string]: IAssetStatus } = jobs.reduce((container, job) => {
        const { _id, actionType, actionStatus } = job;
        // check for completed upload asset statuses
        if (actionType === 'upload' && actionStatus === 'completed' && !completedUploads[_id]) {
          return {
            ...container,
            [_id]: job,
          };
        }
        // ignore if status exists
        return container;
      }, {});
      // Only show toasts for console uploads, already get toasts from client upload
      const consoleUploads = Object.keys(newCompletedUploads).filter((key) => newCompletedUploads[key].actionMachine);
      const newCompletedLength = Object.keys(newCompletedUploads).length;
      if (!initialQuery && newCompletedLength) {
        if (consoleUploads.length > 0) {
          const assetNames = consoleUploads.map((key) => newCompletedUploads[key].assetName).join(',');
          superAdminToast(isVaultSuperAdmin, `Upload of ${assetNames} complete!`, {
            type: toast.TYPE.SUCCESS,
          });
        }
        // check if search or exchange preview panel in edit mode to avoid disruptions
        if (!isSearchEdit && !isExchangeEdit) {
          const {
            location: {
              pathname: route,
            },
          } = window;
          if (route === Routes.HOME) {
            queryGamesAction()(dispatch, getState);
          } else if (route === Routes.EXCHANGE) {
            queryGames()(dispatch, getState);
          }
        }
      }
      // update completed uploads
      completedUploads = {
        ...newCompletedUploads,
        ...completedUploads,
      };
    }

    // Check if clips are completed
    const oldJobsMap = oldJobs.reduce<Record<string, IAssetStatus>>((map, job) => ({
      ...map,
      [job._id]: job,
    }), {});

    jobs.forEach((job) => {
      if (job.actionType === JobType.Create && job.actionStatus === ActionStatus.Completed) {
        const found = oldJobsMap[job._id];
        if (found && found.actionStatus !== ActionStatus.Completed) {
          // If the job was previously not completed, show a toast
          superAdminToast(false, `Clip successfully created:\n ${job.assetName}`, { type: toast.TYPE.SUCCESS });
        }
      }
    });

    const jobData = getJobData(jobs);
    jobInfo = {
      filteredJobs: getFilteredAndSortedJobs(jobs, jobTab, statusFilter, orderBy, order),
      jobs,
      jobData,
      completedUploads,
      currentJobData: getCurrentJobData(jobData, jobTab),
    };

    dispatch({
      type: ActionType.UpdateJobs,
      payload: jobInfo,
    });
  }

  dispatch({
    type: ActionType.FinishLoading,
  });

  await handleNewClipDownloads(state, dispatch, isVaultSuperAdmin, jobInfo, jobTab);
};

export const deleteCompletedJobs = (completedIds: string[], videoType: string[], isVaultSuperAdmin: boolean = false) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  if (completedIds.length > 0) await deleteManyAssetStatus(completedIds);
  await queryJobs(videoType, false, isVaultSuperAdmin)(dispatch, getState);
};

export const clearJobs = () => ({
  type: ActionType.ClearJobs,
});

export const updateSortingOrder = (orderBy: string, order: Order) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const { jobStatus: { jobs, statusFilter, jobTab } } = getState();
  dispatch({
    type: ActionType.UpdateSortingOrder,
    payload: {
      orderBy,
      order,
      filteredJobs: getFilteredAndSortedJobs(jobs, jobTab, statusFilter, orderBy, order),
    },
  });
};

export const toggleStatusFilter = (newStatusFilter: string) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const {
    jobStatus: {
      statusFilter, jobs, orderBy, order, jobTab,
    },
  } = getState();
  if (statusFilter === newStatusFilter) return; // Don't allow toggling to the same filter
  dispatch({
    type: ActionType.ToggleStatusFilter,
    payload: {
      statusFilter: newStatusFilter,
      filteredJobs: getFilteredAndSortedJobs(jobs, jobTab, newStatusFilter, orderBy, order),
    },
  });
};

export const cancelJob = (id: string) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const { jobStatus: { jobData, jobTab } } = getState();
  await deleteAssetStatus(id);
  const newJobData = removeJobFromAllJobData(id, jobData);
  dispatch({
    type: ActionType.RemoveJob,
    payload: {
      id,
      jobData: newJobData,
      currentJobData: getCurrentJobData(newJobData, jobTab),
    },
  });
};

export const updateJobTab = (jobTab: JobTab) => ({
  type: ActionType.UpdateJobTab,
  payload: { jobTab },
});

export const startBackgroundTask = (videoType: string[], isVaultSuperAdmin: boolean) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const state = getState();
  // Check if user has live events access
  const hasLiveEventsAccess = !selectIsExchange(state) && selectHasManageLiveClippingPermission(state);
  // Don't allow multiple intervals running at once
  if (state.jobStatus.backgroundTask) clearInterval(state.jobStatus.backgroundTask);
  // note: check for finished uploads every 15 seconds
  const { setInterval } = window;
  const backgroundTask = setInterval(() => {
    queryJobs(videoType, true, isVaultSuperAdmin, hasLiveEventsAccess ? {} : null)(dispatch, getState);
  }, 15000);
  // Start first query immediately
  queryJobs(videoType, true, isVaultSuperAdmin, hasLiveEventsAccess ? {} : null)(dispatch, getState);

  dispatch({
    type: ActionType.StartBackgroundTask,
    payload: {
      backgroundTask,
    },
  });
};

export const stopBackgroundTask = () => (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const state = getState();
  const {
    jobStatus: {
      backgroundTask,
    },
  } = state;
  if (backgroundTask !== -1) {
    const { clearInterval } = window;

    clearInterval(backgroundTask);

    dispatch({
      type: ActionType.StopBackgroundTask,
    });
  } else {
    console.error('[QA] Attempting to stop background task before initialized');
  }
};

export const openJobStatusReview = () => (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  stopBackgroundTask()(dispatch, getState);
  dispatch({
    type: ActionType.Open,
  });
};

export const closeJobStatusReview = (isVaultSuperAdmin: boolean) => async (
  dispatch: Dispatch,
  getState: () => IAppState,
) => {
  const { product: { target } } = getState();
  dispatch({
    type: ActionType.Close,
  });
  startBackgroundTask(getVideoTypeFromProduct(target), isVaultSuperAdmin)(dispatch, getState);
};

// Gets the failed assetsStatus's asset details, if found retries creating the clip with these details else fails
export const retryCreateAsset = (assetId: string, isVaultSuperAdmin: boolean, jobId?: string) => async (
  dispatch: Dispatch<any>,
  getState: () => IAppState,
) => {
  try {
    dispatch({
      type: ActionType.UpdateRetryDisabled,
      payload: true,
    });

    const assetDetails = await getAssetFromApi(assetId);
    if (assetDetails) {
      let state = getState();
      const {
        user: {
          userOrgRole: {
            loginAs, useAs,
          },
        },
      } = state;

      const role = useAs || loginAs;
      const userHasAccess = role?.userAccessPermission?.manageVideoClip ?? false;

      if (userHasAccess) {
        assetDetails.assetId = assetDetails._id;
        assetDetails.actionType = JobType.Create;
        const res = await postVirtualClipFromApi(assetDetails);
        if (!res?._id) {
          superAdminToast(isVaultSuperAdmin, 'Unable to save clips. Please try again or cancel.', { type: toast.TYPE.ERROR });
        } else {
          console.log(`[QA] Clips saved! New asset id: ${res._id}, title: ${res.title}`);
          try {
            if (!jobId) {
              await queryJobs(['Clip', 'clips', VAULT_VIDEO_TYPE.MyVault], true, isVaultSuperAdmin)(dispatch, getState);
              state = getState();
              const assetJobId = state.jobStatus.jobs?.find((x) => x.assetId === assetId)?._id;
              await deleteAssetStatus(assetJobId);
            } else {
              await deleteAssetStatus(jobId);
            }
          } catch (error) {
            // caught error removing failed asset status and asset
          }
        }
      }
    } else {
      superAdminToast(isVaultSuperAdmin, 'Retry failed for this job, try again from clipping screen', { type: toast.TYPE.ERROR });
    }
  } catch (error) {
    superAdminToast(isVaultSuperAdmin, 'Retry failed for this job, try again from clipping screen', { type: toast.TYPE.ERROR });
  }

  dispatch({
    type: ActionType.UpdateRetryDisabled,
    payload: false,
  });
};

// Retry a failed asset
export const retryAssetJob = (assetId: string, jobType: JobType, isVaultSuperAdmin: boolean, jobId?: string, assetName?: string) => async (
  dispatch: Dispatch<any>,
  getState: () => IAppState,
) => {
  if (jobType === JobType.Create) {
    retryCreateAsset(assetId, isVaultSuperAdmin, jobId)(dispatch, getState);
  } else {
    // VA-2295 - check if asset exists prior to retry
    let assetDetails;
    try {
      assetDetails = await getAssetFromApi(assetId);
    } catch (error) {
      console.error(`[QA] Asset ("${assetId}") has been deleted, skipping retry and deleting associated assetStatus.`);
      superAdminToast(isVaultSuperAdmin, `Retry is not possible, asset ("${assetName || assetId}") has been deleted`, { type: toast.TYPE.WARNING });
      await deleteAssetStatus(jobId);
    }
    if (assetDetails) {
      try {
        await updateAssetStatus(jobId, {
          actionDate: new Date().toISOString(),
          actionStatus: JobStatus.Queued,
        });
      } catch (error) {
        // caught error updating failed asset status
        superAdminToast(isVaultSuperAdmin, 'An error occurred trying to update asset status', { type: toast.TYPE.ERROR });
        console.error('[QA] Error occurred updating asset status: ', error.toString());
      }
    }
  }
};

export const selectActiveUploads = (state: IAppState): Array<IAssetStatus> => {
  const { jobStatus: { jobs } } = state;
  const userRole = selectUserRole(state);
  // note: selects user's web uploads assuming the web client doesn't specify a machine name in the job status
  return jobs ? jobs.filter(({
    user,
    actionType,
    actionStatus,
    actionMachine,
  }) => actionType === JobType.Upload && actionStatus === JobStatus.InProgress && !actionMachine && user === userRole.userInfo.displayName) : [];
};

export const selectQueuedAndInProgressUploads = (state: IAppState): Array<IAssetStatus> => {
  const { jobStatus: { jobs } } = state;
  const userRole = selectUserRole(state);
  // note: selects user's web uploads assuming the web client doesn't specify a machine name in the job status
  return jobs ? jobs.filter(({
    user,
    actionType,
    actionStatus,
    actionMachine,
  }) => actionType === JobType.Upload && (actionStatus === JobStatus.InProgress || actionStatus === JobStatus.Queued) && !actionMachine && user === userRole.userInfo.displayName) : [];
};

export const selectFailedUploads = (state: IAppState): Array<IAssetStatus> => {
  const { jobStatus: { jobs } } = state;
  const userRole = selectUserRole(state);
  // note: selects user's web uploads assuming the web client doesn't specify a machine name in the job status
  return jobs ? jobs.filter(({
    user,
    actionType,
    actionStatus,
    actionMachine,
  }) => actionType === JobType.Upload && actionStatus === JobStatus.Failed && !actionMachine && user === userRole.userInfo.displayName) : [];
};

export const selectJobStatusIsOpen = (state: IAppState): boolean => (state.jobStatus.isOpen);

export const selectCurrentJobs = (state: IAppState): Array<IAssetStatus> => {
  if (state.jobStatus !== undefined && state.jobStatus.jobs !== undefined && state.jobStatus.jobs.length > 0) {
    return [...state.jobStatus.jobs];
  }
  return [];
};

export const selectNumberCurrentJobs = (state: IAppState): number => {
  if (state.jobStatus !== undefined && state.jobStatus.jobs !== undefined && state.jobStatus.jobs.length > 0) {
    const jobs = [...state.jobStatus.jobs];
    return jobs.length;
  }
  return 0;
};

export const handleNewClipDownloads = async (state: IAppState, dispatch: Dispatch, isVaultSuperAdmin: boolean, jobInfo: IJobInfo, jobTab: JobTab) => {
  if (!jobInfo) return;
  const userId = selectUserId(state);
  const pendingPromises: Promise<any>[] = [];
  const updatedJobs = jobInfo.jobs;
  const updatedDownloadIds = jobInfo.jobData.downloads.completedIds;
  let jobInfoQueuedDownloads = jobInfo.jobData.downloads.queued;
  const queuedDownloads = jobInfoQueuedDownloads.filter((job) => job.actionType === JobType.Download && job.userId === userId);
  queuedDownloads.forEach((download, index) => {
    const completedJob = jobInfo.jobs.find((job) => job.assetId === download.assetId && job.actionType === JobType.Create && job.actionStatus === JobStatus.Completed);
    if (completedJob) {
      pendingPromises.push(new Promise(async (res) => {
        const { data: { data: downloadLink } } = await getDownloadLink({ id: download.assetId });
        superAdminToast(false, `Downloading "${download.assetName}"`, { type: toast.TYPE.INFO });
        if (!window.open(downloadLink, '_blank')) {
          superAdminToast(false, 'Please disable pop up blocker to complete this action', { type: toast.TYPE.ERROR });
        } else {
          updatedJobs[index].actionStatus = JobStatus.Completed;
          updatedDownloadIds.push(download._id);
          jobInfoQueuedDownloads = jobInfoQueuedDownloads.filter((job) => job._id !== download._id);
          await updateAssetStatus(
            download._id,
            {
              actionStatus: JobStatus.Completed,
              actionProgress: '100',
            },
          );
        }
        res(null);
      }));
    }
  });
  if (pendingPromises.length === 0) return;
  await Promise.all(pendingPromises);

  const jobData: IAllJobData = {
    uploads: jobInfo.jobData.uploads,
    downloads: {
      all: jobInfo.jobData.downloads.all,
      queued: jobInfoQueuedDownloads,
      completed: jobInfo.jobData.downloads.completed,
      failed: jobInfo.jobData.downloads.failed,
      inProgress: jobInfo.jobData.downloads.inProgress,
      active: jobInfo.jobData.downloads.active,
      completedIds: updatedDownloadIds,
    },
    publishes: jobInfo.jobData.publishes,
    creates: jobInfo.jobData.creates,
    exchangeCreates: jobInfo.jobData.exchangeCreates,
  };
  dispatch({
    type: ActionType.UpdateJobs,
    payload: {
      jobs: updatedJobs,
      jobData,
      completedUploads: jobInfo.completedUploads,
      currentJobData: getCurrentJobData(jobData, jobTab),
    },
  });
};

export const selectNumberCurrentUploads = (state: IAppState): number => selectQueuedAndInProgressUploads(state).length;
